import React from 'react';
import styles from './TimetableDisplay.module.css';

interface TimetableEntry {
  startTime: string;
  duration: string;
  type: 'round' | 'break' | 'welcome' | 'closing' | 'other';
  description?: string;
}

interface Timetable {
  entries: TimetableEntry[];
}

interface TimetableDisplayProps {
  timetable: Timetable;
}

const TimetableDisplay: React.FC<TimetableDisplayProps> = ({ timetable }) => {
  const formatTime = (timeString: string): string => {
    try {
      // Handle different time formats
      let date: Date;
      
      if (timeString.includes('T')) {
        // ISO datetime format
        date = new Date(timeString);
      } else if (timeString.includes(':')) {
        // Time only format (HH:mm or HH:mm:ss)
        const today = new Date();
        const [hours, minutes] = timeString.split(':').map(Number);
        date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
      } else {
        // Fallback
        date = new Date(timeString);
      }
      
      return date.toLocaleTimeString('en-GB', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return timeString; // Return original if parsing fails
    }
  };

  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'round':
        return 'bi-people';
      case 'break':
        return 'bi-cup-hot';
      case 'welcome':
        return 'bi-emoji-smile';
      case 'closing':
        return 'bi-flag';
      default:
        return 'bi-info-circle';
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'round':
        return 'Round';
      case 'break':
        return 'Break';
      case 'welcome':
        return 'Welcome';
      case 'closing':
        return 'Closing';
      default:
        return 'Other';
    }
  };

  const getTypeBadgeClass = (type: string): string => {
    switch (type) {
      case 'round':
        return 'bg-primary';
      case 'break':
        return 'bg-secondary';
      case 'welcome':
        return 'bg-success';
      case 'closing':
        return 'bg-danger';
      default:
        return 'bg-info';
    }
  };

  const getDefaultDescription = (type: string): string => {
    switch (type) {
      case 'round':
        return 'Networking Round';
      case 'break':
        return 'Short Break';
      case 'welcome':
        return 'Welcome Session';
      case 'closing':
        return 'Closing Remarks';
      default:
        return 'Event Activity';
    }
  };

  return (
    <div className={styles.viewContainer}>
      <div className={styles.viewHeader}>
        <h3 className={styles.viewTitle}>Event Schedule</h3>
      </div>
      
      <div className={styles.timeline}>
        {timetable.entries && timetable.entries.length > 0 ? (
          timetable.entries.map((entry, index) => (
            <div 
              key={index} 
              className={`${styles.timelineItem} ${styles[`timeline${entry.type.charAt(0).toUpperCase() + entry.type.slice(1)}`]}`}
            >
              <div className={styles.timelineTime}>
                <div className={styles.timeMain}>{formatTime(entry.startTime)}</div>
                <div className={styles.timeUnit}>p.m.</div>
                <div className={styles.timeDuration}>{entry.duration}</div>
              </div>
              
              <div className={styles.timelineContent}>
                <div className={styles.timelineIcon}>
                  <i className={getTypeIcon(entry.type)}></i>
                </div>
                
                <div className={styles.timelineDetails}>
                  <div className={styles.timelineType}>
                    <span className={`badge ${getTypeBadgeClass(entry.type)}`}>
                      {getTypeLabel(entry.type)}
                    </span>
                    <span className={`${styles.timelineDescription} ${!entry.description ? 'text-muted' : ''}`}>
                      {entry.description || getDefaultDescription(entry.type)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.timelineEmpty}>
            <div className={styles.timelineIcon}>
              <i className="bi bi-calendar-x"></i>
            </div>
            <div className={styles.timelineMessage}>
              No schedule available for this event yet.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimetableDisplay;
